import { useTranslation } from 'react-i18next';
import Collapsible from '@/components/Collapsible';
import reportIcon from '@/assets/report.svg';

const Parameter = ({ label, value, t }) => {
  // Try to get translation for the parameter label, fallback to formatted label
  const translatedLabel = t(`card.parameter_labels.${label}`, { defaultValue: label.replace(/([A-Z])/g, ' $1').trim() });

  // For the type parameter, translate the cardType value
  const translatedValue = label === 'type'
    ? t(`card.card_types.${value}`, { defaultValue: value })
    : value;

  return (
    <div className="flex gap-2">
      <div className="w-[100px] text-[#aaaaaa] text-sm">
        {translatedLabel}
      </div>
      <div className="flex-1 text-[#2b2c3c] text-sm">
        {translatedValue}
      </div>
    </div>
  )
}

const CardParametersSection = ({ parameters, rarityInfo, showReport = false, onReportClick }) => {
  const { t } = useTranslation();

  // Helper function to render rarity display
  const renderRarity = () => {
    if (!rarityInfo) return null;
    
    const parts = [];
    let hasMainIcon = false;
    let hasSubIcon = false;
    
    // Add main rarity
    if (rarityInfo.main) {
      if (rarityInfo.main.icon?.url) {
        hasMainIcon = true;
        parts.push(
          <img 
            key="main-icon" 
            src={rarityInfo.main.icon.url} 
            alt={rarityInfo.main.name || 'rarity'} 
            className="w-4 h-4 inline-block"
          />
        );
      } else if (rarityInfo.main.name) {
        parts.push(
          <span key="main-name">{rarityInfo.main.name}</span>
        );
      }
    }
    
    // Add sub rarity with appropriate separator
    if (rarityInfo.sub) {
      if (parts.length > 0) {
        if (rarityInfo.sub.icon?.url) {
          hasSubIcon = true;
          // No separator for images
        } else if (rarityInfo.sub.name) {
          // Space separator for text
          parts.push(<span key="separator"> </span>);
        }
      }
      
      if (rarityInfo.sub.icon?.url) {
        parts.push(
          <img 
            key="sub-icon" 
            src={rarityInfo.sub.icon.url} 
            alt={rarityInfo.sub.name || 'rarity'} 
            className="w-4 h-4 inline-block"
          />
        );
      } else if (rarityInfo.sub.name) {
        parts.push(
          <span key="sub-name">{rarityInfo.sub.name}</span>
        );
      }
    }
    
    // Use different gap based on whether we have images or not
    const gapClass = hasMainIcon || hasSubIcon ? 'gap-0' : 'gap-1';
    return parts.length > 0 ? <div className={`flex items-center ${gapClass}`}>{parts}</div> : null;
  };

  // Create ordered parameters with rarity inserted after code
  const orderedParams = [];
  let rarityAdded = false;
  
  Object.entries(parameters).forEach(([key, value]) => {
    if (value) {
      orderedParams.push([key, value]);
      
      // Add rarity after code
      if (key === 'code' && !rarityAdded) {
        const rarityDisplay = renderRarity();
        if (rarityDisplay) {
          orderedParams.push(['rarity', rarityDisplay]);
          rarityAdded = true;
        }
      }
    }
  });
  
  // If no code parameter exists but we have rarity info, add it at the end
  if (!rarityAdded) {
    const rarityDisplay = renderRarity();
    if (rarityDisplay) {
      orderedParams.push(['rarity', rarityDisplay]);
    }
  }

  return (
    <Collapsible title={t('card.parameters')} defaultOpen className="border-0 rounded-none border-t">
      <div className="grid grid-cols-2 gap-y-[21px] gap-x-4">
        {orderedParams.map(([key, value]) => (
          key === 'rarity' ? (
            <div key={key} className="flex gap-2">
              <div className="w-[100px] text-[#aaaaaa] text-sm">
                レアリティ
              </div>
              <div className="flex-1 text-[#2b2c3c] text-sm">
                {value}
              </div>
            </div>
          ) : (
            <Parameter
              key={key}
              label={key}
              value={value}
              t={t}
            />
          )
        ))}
      </div>
      {showReport && (
        <div className="flex justify-end mt-4">
          <button 
            className="inline-flex items-center gap-1 text-[#aaaaaa] text-sm"
            onClick={onReportClick}
          >
            <img src={reportIcon} alt="report" className="w-4 h-4" />
            {t('card.report')}
          </button>
        </div>
      )}
    </Collapsible>
  );
};

export default CardParametersSection; 